<template>
  <div class="p-field">
    <div class="p-field-horizontal">
      <label v-if="label" :for="id" class="label-text">
        {{ label }}
        <span v-if="required" class="text-red-500">*</span>
      </label>
      <div class="p-inputswitch-wrapper" :class="{ 'p-invalid': isInvalid }">
        <ToggleSwitch ref="inputRef" :id="id" v-model="localValue" :disabled="disabled" :readonly="readonly"
          :required="required" :trueValue="trueValue" :falseValue="falseValue" :aria-label="props['aria-label']"
          :aria-labelledby="props['aria-labelledby']" :aria-invalid="isInvalid" :aria-required="required"
          :aria-describedby="ariaDescribedBy" :invalid="isInvalid" :pt="{
            root: { class: [wrapperClass, { 'p-invalid': !localValue }] }
          }" @input="onInput" @change="onChange" @focus="(e: any) => onFocus(e)" @blur="(e: any) => onBlur(e)" />
        <label v-if="onLabel || offLabel" class="p-inputswitch-labels">
          <span class="p-inputswitch-on-label" :class="{ 'p-inputswitch-label-active': localValue === trueValue }">{{
            onLabel }}</span>
          <span class="p-inputswitch-off-label" :class="{ 'p-inputswitch-label-active': localValue === falseValue }">{{
            offLabel }}</span>
        </label>
      </div>
    </div>

    <!-- Error Messages -->
    <div v-if="showErrorMessages" class="p-error-messages mt-1">
      <small v-if="helperText" :id="helperTextId" class="p-helper-text block">{{ helperText }}</small>
      <small v-if="isInvalid" :id="`${id}-error`" class="p-error block text-red-500 mt-1">
        {{ getFormattedErrorMessage }}
      </small>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import ToggleSwitch from 'primevue/toggleswitch'

export default defineComponent({
  name: 'MOPInputSwitch',
  components: {
    ToggleSwitch
  },
  inheritAttrs: false
})
</script>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { useFormContext } from '../../../../composables/useFormContext'
import { useLocalization } from '../../../../composables/useLocalization'
import { useVeeValidate } from '../../../../composables/useVeeValidate'
import type { MOPInputSwitchProps } from './types'

const props = withDefaults(defineProps<MOPInputSwitchProps>(), {
  modelValue: undefined,
  trueValue: true,
  falseValue: false,
  onLabel: undefined,
  offLabel: undefined,
  label: undefined,
  helperText: '',
  invalid: false,
  required: false,
  id: () => `mop-inputswitch-${Math.random().toString(36).substr(2, 9)}`,
  disabled: false,
  readonly: false,
  'aria-label': '',
  'aria-labelledby': '',
  validationRules: () => [],
  validateOnBlur: true,
  validateOnInput: false,
  validateOnChange: true,
  showErrors: true,
  name: undefined,
  formContext: true,
  wrapperClass: undefined,
  validationType: undefined,
  customRules: undefined
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: any): void
  (e: 'input', event: Event): void
  (e: 'change', event: Event): void
  (e: 'focus', event: Event): void
  (e: 'blur', event: Event): void
  (e: 'validation-error', errors: string[]): void
  (e: 'validation-success'): void
}>()

// Form Context
const { formState } = useFormContext()
const { t } = useLocalization()

// Local State
const inputRef = ref<HTMLInputElement | null>(null)
const localValidationErrors = ref<string[]>([])
const isDirty = ref(false)
const isValidating = ref(false)

// Setup VeeValidate
const {
  fieldValue,
  errorMessage,
  shouldShowError,
  validate,
  handleValidation,
  updateValue,
  isEmpty,
  touched
} = useVeeValidate({
  name: props.name || props.id,
  modelValue: props.modelValue,
  required: props.required,
  validationType: props.validationType,
  customRules: props.customRules
})

// Ensure fieldValue is properly typed for ToggleSwitch
const typedFieldValue = computed(() => {
  return fieldValue.value as boolean | string | undefined
})

// Watch for external modelValue changes
watch(() => props.modelValue, (newVal) => {
  updateValue(newVal)
}, { immediate: true })

const localValue = computed({
  get: () => typedFieldValue.value,
  set: (newValue) => {
    updateValue(newValue)
    emit('update:modelValue', newValue)
    isDirty.value = true

    if (props.formContext && formState && props.name) {
      formState.setFieldValue(props.name, newValue)

      if (props.validateOnInput) {
        formState.validateField(props.name)
      }
    }

    // Validate immediately if the field is required and the new value is empty
    if (props.required && isEmpty(newValue)) {
      validateInputSwitch()
    }
  }
})

const currentErrors = computed(() => {
  if (props.formContext && formState && props.name) {
    return formState.errors.value[props.name] || []
  }
  return localValidationErrors.value
})

const localizedErrors = computed(() => {
  return currentErrors.value.map(error => {
    if (error.startsWith('validation.')) {
      return t(error)
    }
    if (error.toLowerCase().includes('required')) {
      return t('validation.required')
    }
    return error
  })
})

// Format error message based on validation type
const getFormattedErrorMessage = computed(() => {
  // If we have validation errors from the form context or local validation, use those
  if (currentErrors.value.length > 0) {
    return currentErrors.value[0]
  }

  // If we have an error message from VeeValidate, use that
  if (errorMessage.value) {
    return errorMessage.value
  }

  // If the field is required and empty, show a required message
  if (props.required && isEmpty(localValue.value)) {
    return `${props.label || props.name || 'This field'} is required`
  }

  // Default validation messages based on validation type
  switch (props.validationType) {
    case 'email':
      return 'Please enter a valid email address'
    case 'phone':
      return 'Please enter a valid phone number'
    case 'password':
      return 'Password must contain at least 8 characters, including uppercase, lowercase, number and special character'
    case 'url':
      return 'Please enter a valid URL'
    case 'numeric':
      return 'Please enter numbers only'
    case 'alpha':
      return 'Please enter letters only'
    case 'alphanumeric':
      return 'Please enter letters and numbers only'
    default:
      return ''
  }
})

const isInvalid = computed<boolean>(() => {
  return Boolean(props.invalid || shouldShowError.value || (currentErrors.value.length > 0 && isDirty.value))
})

const helperTextId = computed(() => `${props.id}-helper`)

const ariaDescribedBy = computed((): string => {
  const ids: string[] = []
  if (props.helperText) ids.push(helperTextId.value)
  currentErrors.value.forEach((_, index) => ids.push(`${props.id}-error-${index}`))
  return ids.join(' ')
})

const showErrorMessages = computed(() => {
  return props.showErrors && (
    !!props.helperText ||
    shouldShowError.value ||
    (currentErrors.value.length > 0 && isDirty.value) ||
    (props.required && isEmpty(localValue.value) && isDirty.value)
  )
})

const validateInputSwitch = async (): Promise<boolean> => {
  if (props.formContext && formState && props.name) {
    return formState.validateField(props.name)
  }

  isValidating.value = true
  try {
    const errors: string[] = []
    const value = localValue.value

    // Built-in required validation for input switch
    if (props.required) {
      if (isEmpty(value)) {
        errors.push(`${props.label || props.name || 'This field'} is required`)
        isDirty.value = true
      }
    }

    for (const rule of props.validationRules) {
      let isValid = true
      const message = rule.message

      if (rule.validator) {
        isValid = await rule.validator(value)
      }

      if (!isValid && message) {
        errors.push(message)
      }
    }

    localValidationErrors.value = errors
    if (errors.length > 0) {
      emit('validation-error', errors)
      return false
    }

    emit('validation-success')
    return true
  } finally {
    isValidating.value = false
  }
}

const onInput = async (event: Event) => {
  emit('input', event)
  isDirty.value = true

  if (props.formContext && props.validateOnInput && formState && props.name) {
    await formState.validateField(props.name)
  }

  if (shouldShowError.value) {
    await validate()
  }
}

const onFocus = (event: any) => {
  emit('focus', event)
}

const onBlur = async (event: any) => {
  emit('blur', event)
  isDirty.value = true
  touched.value = true

  if (props.formContext && formState && props.name) {
    formState.setFieldTouched(props.name, true)
    if (props.validateOnBlur) {
      await formState.validateField(props.name)
    }
  }

  await handleValidation()
}

const onChange = async (event: Event) => {
  emit('change', event)
  isDirty.value = true

  if (props.formContext && props.validateOnChange && formState && props.name) {
    await formState.validateField(props.name)
  }

  await validate()
}

// Initialize form context
onMounted(() => {
  if (props.formContext && formState && props.name) {
    formState.addValidation(props.name, props.validationRules)
  }

  // Validate on mount if the field is required and empty
  if (props.required && isEmpty(localValue.value)) {
    isDirty.value = true
    validateInputSwitch()
  }
})

// Watch for external validation triggers
watch(() => props.invalid, (newValue) => {
  if (newValue) {
    isDirty.value = true
  }
})

// Watch for required field changes to trigger validation
watch(() => props.required, (isRequired) => {
  if (isRequired) {
    if (isEmpty(localValue.value)) {
      isDirty.value = true
      validateInputSwitch()
    }
  }
}, { immediate: true })

defineExpose({
  focus: () => inputRef.value?.focus(),
  blur: () => inputRef.value?.blur(),
  validate: validateInputSwitch,
  validationErrors: currentErrors,
  errorMessage,
  isDirty,
  isValidating,
  isValid: computed(() => !isInvalid.value)
})
</script>

<style scoped>
.p-inputswitch-wrapper.p-invalid :deep(.p-toggleswitch) {
  outline: 2px solid #f44336;
  outline-offset: 1px;
}

.p-inputswitch-wrapper.p-invalid :deep(.p-toggleswitch:hover) {
  outline: 2px solid #f44336;
}
</style>
